import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import SplashLoader from '../components/splash/SplashLoader';
import SplashModal from '../components/splash/SplashModal';
import SplashNavbar from '../components/splash/SplashNavbar';
import PromoSections from '../components/splash/PromoSections';
import AuthModal from '../components/auth/AuthModal';
import DevAccessModal from '../components/auth/DevAccessModal';
import './SplashPage.css';

// Decorative "graph points" behind the hero, snapped to the 64px grid drawn in SplashPage.css.
const GRID_CELL = 64;
const POINT_COUNT = 4;
const POINT_DURATIONS = ['6.5s', '7.2s', '7.8s', '8.4s'];

function generateGridPoints() {
  if (typeof window === 'undefined') return [];
  const cols = Math.max(4, Math.floor(window.innerWidth / GRID_CELL));
  const rows = Math.max(4, Math.floor(window.innerHeight / GRID_CELL));
  const used = new Set();
  const points = [];

  while (points.length < POINT_COUNT && used.size < cols * rows) {
    const col = Math.floor(Math.random() * cols);
    const row = Math.floor(Math.random() * rows);
    const key = `${col}-${row}`;
    if (used.has(key)) continue;
    used.add(key);
    points.push({
      left: `${col * GRID_CELL}px`,
      top: `${row * GRID_CELL}px`,
      size: 4 + Math.round(Math.random() * 2),
      delay: `${(Math.random() * 2.4).toFixed(1)}s`,
      duration: POINT_DURATIONS[points.length % POINT_DURATIONS.length],
    });
  }

  return points;
}

export default function SplashPage() {
  const [loaded, setLoaded] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [authMode, setAuthMode] = useState(null);
  const [devAccessOpen, setDevAccessOpen] = useState(false);
  const [promoRevealed, setPromoRevealed] = useState(false);
  const [gridPoints] = useState(generateGridPoints);

  useLayoutEffect(() => {
    const previousRestoration = window.history.scrollRestoration;
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);

    return () => {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = previousRestoration;
      }
    };
  }, []);

  useEffect(() => {
    if (!loaded) return;
    window.scrollTo(0, 0);
  }, [loaded]);

  useEffect(() => {
    if (!loaded) return undefined;

    const promoSections = document.getElementById('promo-sections');
    if (!promoSections) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setShowNavbar(entry.isIntersecting),
      { threshold: 0.01 },
    );

    observer.observe(promoSections);
    return () => observer.disconnect();
  }, [loaded]);

  const handleLearnMore = useCallback(() => {
    requestAnimationFrame(() => {
      const promoSections = document.getElementById('promo-sections');
      if (!promoSections) return;

      setPromoRevealed(true);
      promoSections.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  if (!loaded) {
    return <SplashLoader onComplete={() => setLoaded(true)} />;
  }

  return (
    <div className="splash-page">
      <SplashNavbar
        visible={showNavbar}
        onJoinWaitlist={() => setAuthMode('waitlist')}
        onBetaAccess={() => setAuthMode('betaTester')}
        onDevAccess={() => setDevAccessOpen(true)}
      />

      <div className="splash-page__content">
        <div className="splash-page__hero">
          <div className="splash-page__grid-points" aria-hidden="true">
            {gridPoints.map((point, i) => (
              <span
                key={i}
                className="splash-page__grid-point"
                style={{
                  top: point.top,
                  left: point.left,
                  width: point.size,
                  height: point.size,
                  '--point-duration': point.duration,
                  '--point-delay': point.delay,
                }}
              />
            ))}
          </div>

          <SplashModal
            onLearnMore={handleLearnMore}
            onJoinWaitlist={() => setAuthMode('waitlist')}
            onBetaAccess={() => setAuthMode('betaTester')}
            isLeaving={promoRevealed}
          />
        </div>

        <div className="splash-page__below">
          <PromoSections
            onJoinWaitlist={() => setAuthMode('waitlist')}
            onBetaAccess={() => setAuthMode('betaTester')}
          />
        </div>
      </div>

      {authMode && <AuthModal initialMode={authMode} onClose={() => setAuthMode(null)} />}
      {devAccessOpen && <DevAccessModal onClose={() => setDevAccessOpen(false)} />}
    </div>
  );
}
